import { useCallback } from "react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { StyleSheet, View } from "react-native";
import { useQuery } from "@tanstack/react-query";

import { Button, Panel, Table, Typography, ColumnOptions } from "@smartrent/ui";
import { Plus } from "@smartrent/icons";

import { instance, useIsDesktop } from "@/lib/hooks";
import { HardwareOrder } from "@/types";
import { formatDateToBeHumanReadable } from "@/utils";
import { useDrawerNav } from "@/common/AppDrawer";
import { useUser } from "@/layout/Context";

import { AddressLine } from "./AddressLine";

interface HardwareOrdersTableProps {
  organizationId?: number;
  groupId?: number;
}

export const HardwareOrdersTable: React.FC<HardwareOrdersTableProps> = ({
  organizationId,
  groupId,
}) => {
  const history = useHistory();
  const drawer = useDrawerNav();
  const user = useUser();
  const canCreateOrders = user?.permissions?.create_hardware_orders;
  const isDesktop = useIsDesktop();

  const fetchHardwareOrders = useCallback(
    async (queryParams: Record<string, any>) => {
      const params: Record<string, any> = {
        ...queryParams,
        is_submitted: true, // Only show submitted orders
      };

      if (organizationId) {
        params.organization_id = organizationId;
      }

      if (groupId) {
        params.group_id = groupId;
      }

      const { data } = await instance.get("/hardware-orders", {
        params,
      });

      return data;
    },
    [organizationId, groupId]
  );

  const handleRowPress = (order: HardwareOrder) => {
    drawer.push("hardwareOrderView", order);
  };

  const handleNewOrder = () => {
    if (groupId) {
      history.push(`/groups/${groupId}/hardware-orders/new`);
    } else {
      history.push("/hardware-orders/new");
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["hardware-orders", { organizationId, groupId }],
    queryFn: () => fetchHardwareOrders({}),
  });

  const columns: ColumnOptions<HardwareOrder>[] = [
    {
      name: "order_number",
      header: "Order Number",
      sortable: true,
      filterType: {
        type: "textInputField",
      },
      render: (input) => {
        const row = input.row as HardwareOrder;
        return <Typography type="body">{row.order_number}</Typography>;
      },
    },
    {
      name: "submitted_at",
      header: "Date Created",
      sortable: true,
      render: (input) => {
        const row = input.row as HardwareOrder;
        return (
          <Typography type="body">
            {formatDateToBeHumanReadable({ date: row.submitted_at })}
          </Typography>
        );
      },
    },
    {
      name: "ordered_by_user",
      header: "Created By",
      render: (input) => {
        const row = input.row as HardwareOrder;
        if (!row.order_subtotal) {
          return <Typography type="body">{row.contact_info_name}</Typography>;
        }
        return (
          <Typography type="body">
            {row.ordered_by_user
              ? `${row.ordered_by_user.first_name} ${row.ordered_by_user.last_name}`
              : "N/A"}
          </Typography>
        );
      },
    },
    {
      name: "shipping_address_line1",
      header: "Shipping Address",
      render: (input) => {
        const row = input.row as HardwareOrder;
        const isCustom = row.is_custom_shipping_address;
        const textAlign = !isDesktop ? ("right" as const) : ("left" as const);
        return (
          <View style={isDesktop ? styles.addressFull : styles.addressPhone}>
            <AddressLine isCustom={isCustom} style={{ textAlign }}>
              {row.shipping_address_line1}
            </AddressLine>
            {row.shipping_address_line2 ? (
              <AddressLine isCustom={isCustom} style={{ textAlign }}>
                {row.shipping_address_line2}
              </AddressLine>
            ) : null}
            <AddressLine isCustom={isCustom} style={{ textAlign }}>
              {row.shipping_address_city}, {row.shipping_address_state}{" "}
              {row.shipping_address_zip}
            </AddressLine>
          </View>
        );
      },
    },
    {
      name: "order_subtotal",
      header: "Total",
      sortable: true,
      render: (input) => {
        const row = input.row as HardwareOrder;
        return (
          <Typography type="body">
            ${((row.order_subtotal || 0) / 100).toFixed(2)}
          </Typography>
        );
      },
    },
    {
      name: "order_type",
      header: "Order Type",
      render: ({ row }) => {
        return (
          <Typography>
            {row.order_subtotal ? "Hardware" : "Warranty"}
          </Typography>
        );
      },
    },
  ];
  return (
    <Panel>
      <View style={styles.header}>
        <Typography type="title2">Hardware Orders</Typography>
        {canCreateOrders && (
          <Button
            iconLeft={Plus}
            onPress={handleNewOrder}
            size="small"
            testID="new-hardware-order-button"
          >
            New Order
          </Button>
        )}
      </View>

      <Table<HardwareOrder>
        columns={columns}
        data={data?.records || []}
        loading={isLoading}
        onRowPress={handleRowPress}
        keyExtractor={(item) => item.id.toString()}
      />
    </Panel>
  );
};

const styles = StyleSheet.create({
  addressFull: {
    width: "100%",
  },
  addressPhone: {
    width: "60%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
});
